import {
  imgBaseUrl as IMG_BASEURL,
  heicFileFormats,
  allowedImageTypes,
} from "@/app/constants/constants";
import { customToast } from "@/components/alerts/CustomToast";

type HeicModule = typeof import("heic-to");
type HeicTo = HeicModule["heicTo"];

const HEIC_BRANDS = new Set([
  "heic",
  "heix",
  "hevc",
  "hevx",
  "heim",
  "heis",
  "hevm",
  "hevs",
  "mif1",
  "msf1",
]);

let heicToPromise: Promise<HeicTo> | null = null;

/**
 * Lazily loads the HEIC conversion helper and caches the module export.
 *
 * @returns {Promise<HeicTo>} A promise that resolves to the `heicTo` converter.
 */
const loadHeicTo = (): Promise<HeicTo> => {
  if (!heicToPromise) {
    heicToPromise = import("heic-to").then(({ heicTo }) => heicTo);
  }

  return heicToPromise;
};

/**
 * Extracts a lowercase file extension from a filename.
 *
 * @param {string} filename The file name to inspect.
 * @returns {string} The detected extension, or `pdf` when unavailable.
 */
export const getExtension = (filename: string): string => {
  if (typeof filename !== "string" || !filename) {
    return "pdf";
  }

  return filename.split(".").pop()?.toLowerCase() || "pdf";
};

/**
 * Determines whether a file is a HEIC/HEIF image by checking MIME type,
 * extension, and file header metadata.
 *
 * @param {File | undefined} file The file to inspect.
 * @returns {Promise<boolean>} A promise that resolves to `true` when the file matches a HEIC format.
 */
export const isHeicFile = async (file: File | undefined): Promise<boolean> => {
  if (!file) {
    return false;
  }

  const mimeSubtype = file.type.split("/").pop()?.toLowerCase();
  if (mimeSubtype && heicFileFormats.includes(mimeSubtype)) {
    return true;
  }

  const extension = getExtension(file.name);
  if (heicFileFormats.includes(extension)) {
    return true;
  }

  const headerBuffer = await file.slice(4, 12).arrayBuffer();
  const brand = new TextDecoder().decode(headerBuffer.slice(4)).toLowerCase();

  return headerBuffer.byteLength === 8 && HEIC_BRANDS.has(brand);
};

/**
 * Re-encodes an image blob into a smaller image format using a canvas.
 *
 * @param {Blob} blob The source image blob.
 * @param {"jpeg" | "webp" | "png"} targetFormat The output image format.
 * @param {number} quality The encoder quality passed to `canvas.toBlob`.
 * @returns {Promise<Blob>} A promise that resolves to the compressed image blob.
 */
export const compressImage = async (
  blob: Blob,
  targetFormat: "jpeg" | "webp" | "png" = "webp",
  quality = 0.7,
): Promise<Blob> => {
  const imageBitmap = await createImageBitmap(blob);

  const canvas = document.createElement("canvas");
  canvas.width = imageBitmap.width;
  canvas.height = imageBitmap.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas context not available");
  }

  ctx.drawImage(imageBitmap, 0, 0);

  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (compressedBlob) => {
        if (!compressedBlob) {
          reject(new Error("Compression failed"));

          return;
        }
        resolve(compressedBlob);
      },
      `image/${targetFormat}`,
      quality,
    ),
  );
};

/**
 * Converts a HEIC image file into a browser-friendly format.
 *
 * @param {object} params The conversion options.
 * @param {File | string} params.file The source file or existing string value.
 * @param {"jpeg" | "png" | "webp"} [params.targetFormat] The desired output format.
 * @param {number} [params.quality] The output quality, where higher values increase file size.
 * @returns {Promise<File | null>} A promise that resolves to the original file, a converted
 * file, or `null` when conversion is skipped or fails.
 * @throws {Error} Thrown when no file value is provided.
 */
export const convertImageFormat = async ({
  file,
  targetFormat = "jpeg",
  quality = 0.7,
}: {
  file: File | string;
  targetFormat?: "jpeg" | "png" | "webp";
  quality?: number;
}): Promise<File | null> => {
  if (!file) {
    throw new Error("No file provided");
  }
  if (typeof file === "string") {
    return null;
  }
  if (!(await isHeicFile(file))) {
    return file;
  }
  try {
    const heicTo = await loadHeicTo();
    const convertedBlob = await heicTo({
      blob: file,
      type: `image/${targetFormat}`,
      quality,
    });

    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const compressedBlob = await compressImage(
      convertedBlob,
      targetFormat,
      quality,
    );

    const convertedFile = new File(
      [compressedBlob],
      `${fileNameWithoutExt}.${targetFormat}`,
      { type: `image/${targetFormat}` },
    );

    return convertedFile;
  } catch (error) {
    customToast.error("Error converting image file");

    return null;
  }
};

/**
 * Extracts the original file name from an image URL generated with the app's
 * image base URL pattern.
 *
 * @param {string} url The full image URL.
 * @param {string} [imgBaseUrl] An optional base URL override.
 * @returns {string} The decoded file name, or an empty string when it cannot be derived.
 */
export const splitImageFileName = (
  url: string,
  imgBaseUrl?: string,
): string => {
  const baseUrl = imgBaseUrl ?? IMG_BASEURL;
  if (!url?.startsWith(baseUrl)) {
    return "";
  }
  // Remove the base URL from the full URL
  const relativeUrl = imgBaseUrl
    ? url.replace(imgBaseUrl, "")
    : url.split("/").pop();

  if (!relativeUrl) {
    return "";
  }
  // Find the index of the first "-"
  const firstDashIndex = relativeUrl.indexOf("-");

  // Get the substring after the first "-"
  const fileName = relativeUrl.substring(firstDashIndex + 1);

  const decodedURI = decodeURIComponent(fileName);

  if (decodedURI.includes("/")) {
    return decodedURI?.split("/")?.pop() ?? decodedURI;
  }

  return decodedURI;
};

/**
 * Converts a base64 data URL payload into a `Blob`.
 *
 * @param {string} base64 The full data URL string.
 * @param {string} mimeType The MIME type to assign to the blob.
 * @returns {Blob} A blob created from the decoded data, or an empty blob on failure.
 */
export const dataUrlToBlob = (base64: string, mimeType: string): Blob => {
  const parts = base64.split(",");
  if (parts.length < 2) {
    customToast.info("Invalid data URL format.");
  }
  let [, base64String] = parts;
  base64String = base64String.replace(/\s/g, "");

  let decodedByteString: string;
  try {
    decodedByteString = atob(base64String);
  } catch (error) {
    // toast.error("Failed to process data. Corrupted or invalid data format.");

    return new Blob([], { type: mimeType });
  }

  const arrayBuffer = new ArrayBuffer(decodedByteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  for (let i = 0; i < decodedByteString.length; i++) {
    uint8Array[i] = decodedByteString.charCodeAt(i);
  }

  return new Blob([uint8Array], { type: mimeType });
};

/**
 * Opens a file preview in a new browser tab, using online viewers for supported
 * document formats when possible.
 *
 * @param {string} url The public file URL to preview.
 * @returns {void} Does not return a value. This function does not return a value.
 */
export const openFilePreviewInNewTab = (url: string): void => {
  if (!url) {
    customToast.error("No file URL provided");

    return;
  }

  const extension = url.split(".").pop()?.toLowerCase();
  const baseUrl = encodeURIComponent(url);
  const officeViewer = `https://view.officeapps.live.com/op/view.aspx?src=${baseUrl}`;
  const googleViewer = `https://docs.google.com/viewer?url=${baseUrl}`;

  const viewers: Record<string, string> = {
    pdf: googleViewer,
    doc: officeViewer,
    docx: officeViewer,
    xls: officeViewer,
    xlsx: officeViewer,
    ppt: officeViewer,
    pptx: officeViewer,
    csv: googleViewer,
  };

  const viewerUrl = viewers[extension || ""] || url;
  window.open(viewerUrl, "_blank");
};

/**
 * Checks if a file name or URL represents an image based on its extension.
 *
 * @param {string} fileNameOrUrl - The file name or URL to check.
 * @returns {boolean} True if it's an image.
 */
export const isImageFile = (fileNameOrUrl: string): boolean => {
  const cleanUrl = fileNameOrUrl.split(/[?#]/)[0];
  return allowedImageTypes.includes(getExtension(cleanUrl));
};

/**
 * Categorizes an array of items into images and files based on their extension.
 *
 * @param {T[]} items - The items to categorize.
 * @param {(item: T) => string | undefined} getFileName - Function to get the file name or URL from an item.
 * @returns {{ images: T[]; files: T[] }} The categorized items.
 */
export const categorizeFiles = <T>(
  items: T[],
  getFileName: (item: T) => string | undefined,
): { images: T[]; files: T[] } => {
  return items.reduce(
    (acc, item) => {
      const fileName = getFileName(item);
      if (!fileName) {
        return acc;
      }

      if (isImageFile(fileName)) {
        acc.images.push(item);
      } else {
        acc.files.push(item);
      }

      return acc;
    },
    { images: [] as T[], files: [] as T[] },
  );
};
